/**
 * Calendar dates without time or time zone.
 *
 * Scheduling deals in days, never in instants. Using JavaScript `Date` here
 * would drag a time zone into every comparison: a plan calculated in Berlin in
 * summer and read back in winter must not shift by an hour, and a task must not
 * start "yesterday" because the server runs in UTC.
 *
 * Dates are therefore plain `YYYY-MM-DD` strings on the outside and integer day
 * numbers on the inside. All arithmetic happens on those integers, which is
 * exact, has no zone, and no daylight saving.
 *
 * The conversions are the well-known civil/serial-day algorithms; they are valid
 * for the proleptic Gregorian calendar and far beyond any year this tool sees.
 */
export const MONDAY = 1;
export const SATURDAY = 6;
export const SUNDAY = 7;
const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Parses and validates a date. Returns undefined for anything that is not a
 * real calendar date, including `2026-02-30` and `2025-02-29`.
 */
export function parseIsoDate(value) {
    const match = ISO_PATTERN.exec(value);
    if (!match)
        return undefined;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        return undefined;
    if (day < 1 || day > daysInMonth(year, month))
        return undefined;
    return value;
}
/** Throws on an invalid date. For places where a bad value is a programming error. */
export function requireIsoDate(value) {
    const parsed = parseIsoDate(value);
    if (parsed === undefined) {
        throw new Error(`Not a valid calendar date: ${JSON.stringify(value)}`);
    }
    return parsed;
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
export function daysInMonth(year, month) {
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/** Builds a date from its parts, zero-padded. Does not validate. */
export function makeIsoDate(year, month, day) {
    const mm = month < 10 ? `0${month}` : String(month);
    const dd = day < 10 ? `0${day}` : String(day);
    return `${year}-${mm}-${dd}`;
}
export function yearOf(date) {
    return Number(date.slice(0, 4));
}
/**
 * Days since 1970-01-01, which is day 0. Negative before that.
 * Inverse of {@link fromDayNumber}.
 */
export function toDayNumber(date) {
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const day = Number(date.slice(8, 10));
    const y = year - (month <= 2 ? 1 : 0);
    const era = Math.floor(y / 400);
    const yearOfEra = y - era * 400;
    const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
    const dayOfEra = yearOfEra * 365 +
        Math.floor(yearOfEra / 4) -
        Math.floor(yearOfEra / 100) +
        dayOfYear;
    return era * 146097 + dayOfEra - 719468;
}
/** Inverse of {@link toDayNumber}. */
export function fromDayNumber(dayNumber) {
    const z = dayNumber + 719468;
    const era = Math.floor(z / 146097);
    const dayOfEra = z - era * 146097;
    const yearOfEra = Math.floor((dayOfEra -
        Math.floor(dayOfEra / 1460) +
        Math.floor(dayOfEra / 36524) -
        Math.floor(dayOfEra / 146096)) /
        365);
    const year = yearOfEra + era * 400;
    const dayOfYear = dayOfEra -
        (365 * yearOfEra +
            Math.floor(yearOfEra / 4) -
            Math.floor(yearOfEra / 100));
    const mp = Math.floor((5 * dayOfYear + 2) / 153);
    const day = dayOfYear - Math.floor((153 * mp + 2) / 5) + 1;
    const month = mp + (mp < 10 ? 3 : -9);
    return makeIsoDate(year + (month <= 2 ? 1 : 0), month, day);
}
/** Shifts by whole calendar days, weekends and holidays included. */
export function addDays(date, days) {
    return fromDayNumber(toDayNumber(date) + days);
}
/** Calendar days between two dates; negative when `to` lies before `from`. */
export function daysBetween(from, to) {
    return toDayNumber(to) - toDayNumber(from);
}
/** ISO weekday, 1 = Monday ... 7 = Sunday. */
export function weekdayOf(date) {
    // Day number 0 is 1970-01-01, a Thursday (ISO weekday 4).
    const n = toDayNumber(date);
    return ((((n + 3) % 7) + 7) % 7 + 1);
}
export function isBefore(a, b) {
    return a < b;
}
export function isAfter(a, b) {
    return a > b;
}
/** Lexicographic comparison works because the format is fixed width. */
export function compareDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
export function minDate(a, b) {
    return a <= b ? a : b;
}
export function maxDate(a, b) {
    return a >= b ? a : b;
}
//# sourceMappingURL=date.js.map